<script>
  // Simple line chart for a series of { year, value } points.
  // Points may carry extra fields ({ assets, liabilities }) which the
  // hover tooltip surfaces if present.
  // Auto-scales y-axis, handles negative values (unlike BalanceChart which
  // floors at 0 because a loan balance never goes negative).

  let { points, colorVar = "--accent", width = 640, height = 260 } = $props();

  const PADDING = { top: 16, right: 12, bottom: 32, left: 60 };

  let hoveredIndex = $state(null);
  let svgEl;

  function fmtGBPFull(n) {
    const abs = Math.abs(n);
    const sign = n < 0 ? "−" : "";
    return sign + new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(abs);
  }

  function handlePointer(e) {
    if (!svgEl || points.length === 0) return;
    const rect = svgEl.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * width;
    const dataX = xMin + ((svgX - PADDING.left) / plotWidth) * xRange;
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i].year - dataX);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    hoveredIndex = bestIdx;
  }
  function handleLeave() { hoveredIndex = null; }

  const hovered = $derived(
    hoveredIndex != null ? points[hoveredIndex] : null,
  );
  const hoverX = $derived(hovered ? xScale(hovered.year) : null);
  const hoverY = $derived(hovered ? yScale(hovered.value) : null);
  const tooltipLeftPct = $derived.by(() => {
    if (hoverX == null) return 0;
    const pct = (hoverX / width) * 100;
    return Math.max(2, Math.min(98, pct));
  });
  const plotWidth = $derived(width - PADDING.left - PADDING.right);
  const plotHeight = $derived(height - PADDING.top - PADDING.bottom);

  const xMin = $derived(points.length > 0 ? points[0].year : 0);
  const xMax = $derived(points.length > 0 ? points[points.length - 1].year : 1);
  const xRange = $derived(xMax - xMin || 1);

  const rawMin = $derived(
    points.length > 0 ? Math.min(...points.map((p) => p.value)) : 0,
  );
  const rawMax = $derived(
    points.length > 0 ? Math.max(...points.map((p) => p.value)) : 0,
  );
  const yMin = $derived(Math.min(0, rawMin));
  const yMax = $derived(Math.max(0, rawMax));
  const yRange = $derived(yMax - yMin || 1);

  const xScale = (x) => PADDING.left + ((x - xMin) / xRange) * plotWidth;
  const yScale = (y) => PADDING.top + (1 - (y - yMin) / yRange) * plotHeight;
  const zeroY = $derived(yScale(0));

  const linePath = $derived(
    points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.year)} ${yScale(p.value)}`)
      .join(" "),
  );

  const areaPath = $derived(
    points.length > 0
      ? linePath +
          ` L ${xScale(points[points.length - 1].year)} ${zeroY}` +
          ` L ${xScale(points[0].year)} ${zeroY} Z`
      : "",
  );

  const yTicks = $derived(niceTicks(yMin, yMax, 5));
  const xStep = $derived(xRange > 25 ? 10 : 5);
  const xTicks = $derived.by(() => {
    const t = [];
    for (let i = xMin; i <= xMax; i += xStep) t.push(i);
    if (t[t.length - 1] !== xMax) t.push(xMax);
    return t;
  });

  function niceTicks(min, max, count) {
    if (max - min === 0) return [0];
    const rawStep = (max - min) / (count - 1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
    const norm = rawStep / magnitude;
    let niceStep;
    if (norm < 1.5) niceStep = 1;
    else if (norm < 3) niceStep = 2;
    else if (norm < 7) niceStep = 5;
    else niceStep = 10;
    niceStep *= magnitude;
    const startTick = Math.floor(min / niceStep) * niceStep;
    const ticks = [];
    for (let v = startTick; v <= max + niceStep * 0.05; v += niceStep) ticks.push(v);
    return ticks;
  }

  function fmtGBPShort(n) {
    const abs = Math.abs(n);
    const sign = n < 0 ? "−" : "";
    if (abs >= 1_000_000) return sign + "£" + (abs / 1_000_000).toFixed(1) + "M";
    if (abs >= 1_000) return sign + "£" + (abs / 1_000).toFixed(0) + "k";
    return sign + "£" + abs.toFixed(0);
  }
</script>

<div class="chart-wrap">
<svg
  bind:this={svgEl}
  {width}
  {height}
  viewBox="0 0 {width} {height}"
  role="img"
  aria-label="Net worth over time"
  preserveAspectRatio="xMidYMid meet"
  onpointermove={handlePointer}
  onpointerdown={handlePointer}
  onpointerleave={handleLeave}
>
  {#each yTicks as tick (tick)}
    <line
      x1={PADDING.left}
      x2={PADDING.left + plotWidth}
      y1={yScale(tick)}
      y2={yScale(tick)}
      class="grid"
      class:zero-line={tick === 0}
    />
    <text
      x={PADDING.left - 8}
      y={yScale(tick)}
      class="axis-label"
      text-anchor="end"
      dominant-baseline="central"
    >
      {fmtGBPShort(tick)}
    </text>
  {/each}

  {#each xTicks as tick (tick)}
    <text
      x={xScale(tick)}
      y={PADDING.top + plotHeight + 20}
      class="axis-label"
      text-anchor="middle"
    >
      age {tick}
    </text>
  {/each}

  {#if areaPath}
    <path d={areaPath} class="area" style="fill: var({colorVar});" />
  {/if}
  {#if linePath}
    <path d={linePath} class="line" style="stroke: var({colorVar});" />
  {/if}

  {#if hovered != null}
    <line
      x1={hoverX}
      x2={hoverX}
      y1={PADDING.top}
      y2={PADDING.top + plotHeight}
      class="hover-line"
    />
    <circle cx={hoverX} cy={hoverY} r="5" class="hover-dot" />
  {/if}
</svg>

{#if hovered != null}
  <div class="tooltip" style="left: {tooltipLeftPct}%;">
    <div class="tooltip-header">Age {hovered.year}</div>
    <dl class="tooltip-rows">
      <div>
        <dt>Net worth</dt>
        <dd class="tt-strong" style="color: var({colorVar});">
          {fmtGBPFull(hovered.value)}
        </dd>
      </div>
      {#if hovered.assets != null}
        <div>
          <dt>Assets</dt>
          <dd>{fmtGBPFull(hovered.assets)}</dd>
        </div>
      {/if}
      {#if hovered.liabilities != null}
        <div>
          <dt>Liabilities</dt>
          <dd>{fmtGBPFull(hovered.liabilities)}</dd>
        </div>
      {/if}
    </dl>
  </div>
{/if}
</div>

<style>
  .chart-wrap { position: relative; }
  svg {
    display: block;
    width: 100%;
    height: auto;
    max-height: 320px;
    overflow: visible;
    touch-action: pan-y;
    cursor: crosshair;
  }
  .grid {
    stroke: var(--chart-grid);
    stroke-width: 1;
    stroke-dasharray: 2 3;
  }
  .zero-line {
    stroke: var(--chart-axis);
    stroke-width: 1.5;
    stroke-dasharray: none;
  }
  .axis-label {
    fill: var(--fg-muted);
    font-size: 11px;
    font-family: inherit;
    font-variant-numeric: tabular-nums;
  }
  .area { fill-opacity: 0.22; stroke: none; }
  .line {
    fill: none;
    stroke-width: 2.5;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  .hover-line {
    stroke: var(--fg-muted);
    stroke-width: 1;
    stroke-dasharray: 3 3;
    pointer-events: none;
  }
  .hover-dot {
    fill: var(--card);
    stroke: var(--fg);
    stroke-width: 2;
    pointer-events: none;
  }

  .tooltip {
    position: absolute;
    top: 4px;
    transform: translateX(-50%);
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.55rem;
    padding: 0.55rem 0.75rem;
    box-shadow: 0 8px 20px rgba(15, 61, 62, 0.12), 0 2px 4px rgba(15, 61, 62, 0.06);
    font-size: 0.82rem;
    color: var(--fg);
    pointer-events: none;
    min-width: 175px;
    max-width: 240px;
    z-index: 5;
  }
  .tooltip-header {
    font-weight: 700;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--accent);
    margin-bottom: 0.35rem;
  }
  .tooltip-rows { margin: 0; display: flex; flex-direction: column; gap: 0.15rem; }
  .tooltip-rows > div {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
  }
  .tooltip-rows dt {
    color: var(--fg-muted);
    font-size: 0.78rem;
    font-weight: 500;
  }
  .tooltip-rows dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
    font-weight: 600;
  }
  .tt-strong { font-weight: 800 !important; }
</style>
